/**
 * Buchungsstrecke B2 — die Bausteine der neuen Angebotsseite (Mockup Station 4,
 * docs/KONZEPT-VERKAUFSPSYCHOLOGIE.md § 4 Punkt 4). Sie erscheinen NUR, wenn der Server
 * `angebotsseite.v2 === true` liefert (Mandanten-Schalter, sql/591); sonst zeichnet
 * `pages/offer-public.tsx` die Seite wie bisher.
 *
 * Diese Datei rechnet nichts und schickt nichts ab: Beträge kommen fertig vom Server
 * (`proPersonCents`, `amount_total_cents`), die „Frage an …“ läuft über den bestehenden Nachrichtenweg
 * der Seite (POST …/aenderungswunsch, in der Seite), „Teilen“ gibt denselben Gast-Link weiter.
 *
 * Gast-Ansprache „Sie“ (Design-Regel öffentliche Seite). Deutsche Zitate mit „…“.
 */
import { useState, type ReactNode } from "react";
import { Button, Card, Icon } from "./components";
import { GoogleAttribution } from "./GoogleAttribution";
import { money } from "./format";
import type { AngebotsseiteV2 } from "./types";
import { A_LA_CARTE_GAST } from "../lib/a-la-carte";

export const V2T = {
  de: {
    notizTitel: "Persönliche Nachricht",
    ansprechpartnerTitel: "Ihr Ansprechpartner",
    ansprechpartnerTitelMehrere: "Ihre Ansprechpartner",
    vertretung: "Vertretung",
    proPerson: "pro Person",
    gesamt: "gesamt",
    fuerGaeste: (n: number) => `für ${n} Gäste`,
    bewertungenTitel: "Google-Bewertungen",
    bewertungSumme: (note: string, anzahl: string, eins: boolean) => `${note} von 5 · ${anzahl} ${eins ? "Google-Bewertung" : "Google-Bewertungen"}`,
    sterneAria: (note: string) => `${note} von 5 Sternen`,
    // § 5b Abs. 3 UWG: ob und wie der Betrieb sicherstellt, dass Bewertungen von echten Gästen stammen.
    bewertungHinweis: (betrieb: string) =>
      `Die Bewertungen stammen von Google. Eine Auswahl unserer Bewertungen – die Gesamtnote oben umfasst alle Bewertungen.`,
    alleBewertungen: "Alle Bewertungen bei Google",
    frageTitel: "Noch eine Frage?",
    frageAntwort: (name: string, dauer: string) => `${name} antwortet meist innerhalb von ${dauer}.`,
    frageAntwortOhne: "Wir antworten Ihnen persönlich per E-Mail.",
    dauer: (wert: number, einheit: "stunden" | "tage") =>
      einheit === "stunden" ? (wert === 1 ? "einer Stunde" : `${wert} Stunden`) : wert === 1 ? "einem Tag" : `${wert} Tagen`,
    frageKnopf: (vorname: string | null) => (vorname ? `Frage an ${vorname}` : "Frage stellen"),
    anrufen: "Direkt anrufen",
    frageFormTitel: (vorname: string | null) => (vorname ? `Ihre Frage an ${vorname}` : "Ihre Frage an uns"),
    frageFormText: "Schreiben Sie einfach, was Sie wissen möchten. Die Antwort erhalten Sie per E-Mail.",
    fragePlatzhalter: "Ihre Frage …",
    frageSenden: "Frage senden",
    frageDanke: (vorname: string | null) => (vorname ? `Danke! ${vorname} meldet sich bei Ihnen.` : "Danke! Wir melden uns bei Ihnen."),
    frageFehler: "Die Frage konnte nicht gesendet werden. Bitte erneut versuchen.",
    teilen: "Mit Mitentscheider teilen",
    teilenHinweis: "Derselbe Link, den Sie per E-Mail bekommen haben. Mit dem Link kann das Angebot auch angenommen werden.",
    teilenTitel: (betrieb: string) => `Unser Angebot von ${betrieb}`,
    kopiert: "Link kopiert. Sie können ihn jetzt weitergeben.",
    kopierenFehler: "Kopieren war nicht möglich. Der Link:",
    leisteGaeste: (n: number) => `${n} Gäste`,
    leisteZubuchungen: "zzgl. gewählter Zubuchungen",
    leisteKnopf: "Weiter zur Zusage",
    fussAnpassung: "Anpassung wünschen",
    fussAbsagen: "Absagen",
    fussAria: "Weitere Möglichkeiten",
  },
  en: {
    notizTitel: "A personal note",
    ansprechpartnerTitel: "Your contact",
    ansprechpartnerTitelMehrere: "Your contacts",
    vertretung: "Deputy",
    proPerson: "per person",
    gesamt: "total",
    fuerGaeste: (n: number) => `for ${n} guests`,
    bewertungenTitel: "Google reviews",
    bewertungSumme: (note: string, anzahl: string, eins: boolean) => `${note} of 5 · ${anzahl} ${eins ? "Google review" : "Google reviews"}`,
    sterneAria: (note: string) => `${note} of 5 stars`,
    bewertungHinweis: (betrieb: string) =>
      `These reviews come from Google. A selection of our reviews – the overall rating above includes all reviews.`,
    alleBewertungen: "All reviews on Google",
    frageTitel: "Any questions?",
    frageAntwort: (name: string, dauer: string) => `${name} usually replies within ${dauer}.`,
    frageAntwortOhne: "We will reply to you personally by email.",
    dauer: (wert: number, einheit: "stunden" | "tage") =>
      einheit === "stunden" ? (wert === 1 ? "one hour" : `${wert} hours`) : wert === 1 ? "one day" : `${wert} days`,
    frageKnopf: (vorname: string | null) => (vorname ? `Ask ${vorname}` : "Ask a question"),
    anrufen: "Call directly",
    frageFormTitel: (vorname: string | null) => (vorname ? `Your question for ${vorname}` : "Your question"),
    frageFormText: "Just write what you would like to know. You will receive the answer by email.",
    fragePlatzhalter: "Your question …",
    frageSenden: "Send question",
    frageDanke: (vorname: string | null) => (vorname ? `Thank you! ${vorname} will get back to you.` : "Thank you! We will get back to you."),
    frageFehler: "Your question could not be sent. Please try again.",
    teilen: "Share with a co-decider",
    teilenHinweis: "The same link you received by email. The offer can also be accepted with this link.",
    teilenTitel: (betrieb: string) => `Our offer from ${betrieb}`,
    kopiert: "Link copied. You can pass it on now.",
    kopierenFehler: "Copying was not possible. The link:",
    leisteGaeste: (n: number) => `${n} guests`,
    leisteZubuchungen: "plus selected add-ons",
    leisteKnopf: "Continue to confirm",
    fussAnpassung: "Request changes",
    fussAbsagen: "Decline",
    fussAria: "More options",
  },
} as const;

type Sprache = "de" | "en";
type Ansprechpartner = NonNullable<AngebotsseiteV2["ansprechpartner"]>;

/** Vorname für „Frage an Domenico“ — der erste Namensteil, sonst nichts. */
export function vornameAus(a: Ansprechpartner | null | undefined): string | null {
  const erster = a?.name.trim().split(/\s+/)[0];
  return erster ? erster : null;
}

function initialen(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((t) => t[0]?.toUpperCase() ?? "")
    .join("");
}

function Avatar({ a, gross = false }: { a: Ansprechpartner; gross?: boolean }) {
  const groesse = gross ? "h-14 w-14 text-[17px]" : "h-11 w-11 text-[14px]";
  return a.fotoUrl ? (
    <img src={a.fotoUrl} alt={a.name} className={`${groesse} shrink-0 rounded-full object-cover`} />
  ) : (
    <span aria-hidden className={`${groesse} grid shrink-0 place-items-center rounded-full bg-surface-container-high font-semibold text-on-surface-variant dark:bg-inverse-surface dark:text-secondary-fixed-dim`}>
      {initialen(a.name)}
    </span>
  );
}

/** Persönliche Notiz (Anschreiben der versendeten Fassung) mit dem Ansprechpartner als Absender. */
export function PersoenlicheNotiz({ v2, lang }: { v2: AngebotsseiteV2; lang: Sprache }) {
  const t = V2T[lang];
  const a = v2.ansprechpartner;
  if (!v2.notiz && !a) return null;
  const personen = a ? [a, ...(v2.weitere ?? [])] : [];
  const gleich = v2.darstellung === "gleichberechtigt";
  return (
    <Card className="maestro-druck-block p-6 md:p-8" data-testid="v2-notiz">
      <p className="font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
        {v2.notiz ? t.notizTitel : personen.length > 1 ? t.ansprechpartnerTitelMehrere : t.ansprechpartnerTitel}
      </p>
      {v2.notiz && (
        <p className="mt-3 whitespace-pre-line font-serif text-[17px] leading-relaxed text-on-surface dark:text-inverse-on-surface" data-testid="v2-notiz-text">
          {v2.notiz}
        </p>
      )}
      {a && (
        <div className={`flex flex-col gap-4 ${v2.notiz ? "mt-5 border-t border-surface-variant pt-4 dark:border-outline/30" : "mt-3"}`}>
          {personen.map((p, i) => {
            // Antoine 07.10.2026: „gleichberechtigt“ = alle gleich (Foto groß, Direktnummer); sonst
            // Hauptperson + Vertretung (klein, ohne Direktnummer) wie in den Mails.
            const vertretung = !gleich && i > 0;
            return (
              <div key={`${p.name}-${i}`} className="flex items-center gap-3.5" data-testid="v2-ansprechpartner">
                <Avatar a={p} gross={!vertretung} />
                <div className="min-w-0">
                  {vertretung && <p className="font-label-md text-label-md text-secondary dark:text-secondary-fixed-dim">{t.vertretung}</p>}
                  <p className="font-body-md text-body-md font-semibold text-on-surface dark:text-inverse-on-surface">{p.name}</p>
                  {p.rolle && <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{p.rolle}</p>}
                  {p.telefon && !vertretung && (
                    <a
                      href={`tel:${p.telefon.trim().startsWith("+") ? "+" : ""}${p.telefon.replace(/\D/g, "")}`}
                      className="mt-0.5 inline-flex items-center gap-1.5 font-body-sm text-body-sm text-on-surface underline underline-offset-2 dark:text-inverse-on-surface"
                    >
                      <Icon name="call" className="text-[15px]" /> {p.telefon}
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

/**
 * „pro Person“ und „gesamt“ gleichwertig — beide Zahlen vom Server. Fehlt der Betrag je Person
 * (keine Gästezahl), steht nur die Summe da; gerechnet wird hier nichts.
 */
export function PreiseGleichwertig({
  proPersonCents,
  gesamtCents,
  gaeste,
  ausgewaehlt,
  lang,
  aLaCarte = false,
}: {
  proPersonCents: number | undefined;
  gesamtCents: number;
  gaeste: number;
  ausgewaehlt: boolean;
  lang: Sprache;
  /** Migration 731: „À la carte“ - statt Beträgen steht „Preis nach Karte, Abrechnung im Restaurant“. */
  aLaCarte?: boolean;
}) {
  const t = V2T[lang];
  if (aLaCarte) {
    return (
      <div className="mt-auto border-t border-surface-variant pt-3.5 dark:border-outline/30" data-testid="v2-preise" data-einblick-preis="1">
        <p data-testid="v2-preis-a-la-carte" className="font-headline-sm text-headline-sm text-on-surface dark:text-inverse-on-surface">
          {A_LA_CARTE_GAST[lang].preisZeile}
        </p>
      </div>
    );
  }
  const betragKlasse = `font-headline-sm text-headline-sm ${ausgewaehlt ? "text-on-surface dark:text-inverse-on-surface" : "text-on-surface dark:text-inverse-on-surface"}`;
  return (
    <div className="mt-auto border-t border-surface-variant pt-3.5 dark:border-outline/30" data-testid="v2-preise" data-einblick-preis="1">
      <div className={`grid gap-3 ${proPersonCents != null ? "grid-cols-2" : "grid-cols-1"}`}>
        {proPersonCents != null && (
          <div data-testid="v2-preis-pro-person">
            <p className={betragKlasse}>{money(proPersonCents)}</p>
            <p className="font-label-md text-label-md text-secondary dark:text-secondary-fixed-dim">{t.proPerson}</p>
          </div>
        )}
        <div data-testid="v2-preis-gesamt" className={proPersonCents != null ? "text-right" : ""}>
          <p className={betragKlasse}>{money(gesamtCents)}</p>
          <p className="font-label-md text-label-md text-secondary dark:text-secondary-fixed-dim">
            {t.gesamt}
            {gaeste > 0 ? ` · ${t.fuerGaeste(gaeste)}` : ""}
          </p>
        </div>
      </div>
    </div>
  );
}

/** Echte Google-Bewertungen mit Hinweis nach § 5b Abs. 3 UWG. Ohne Bewertungen: nichts. */
export function Bewertungen({ v2, lang, betrieb }: { v2: AngebotsseiteV2; lang: Sprache; betrieb: string }) {
  const b = v2.bewertungen;
  if (!b) return null;
  const t = V2T[lang];
  const zahl = new Intl.NumberFormat(lang === "en" ? "en-GB" : "de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const note = zahl.format(b.sterne);
  const anzahl = new Intl.NumberFormat(lang === "en" ? "en-GB" : "de-DE").format(b.anzahl);
  // Zweitpruefung PR #1506 (N3): nie aufgerundet. Antoine, 02.10.2026: anteilig gefüllt — 4,5 → der
  // fünfte Stern zur Hälfte (je Stern genau der erreichte Anteil, 4,7 → 70 %).
  const sterne = (wert: number) => (
    <span aria-hidden="true" className="inline-flex" data-sterne={wert}>
      {[0, 1, 2, 3, 4].map((i) => {
        const anteil = Math.min(1, Math.max(0, wert - i));
        return (
          <span key={i} className="relative inline-block" data-fuellung={anteil}>
            ☆
            {anteil > 0 && (
              <span className="absolute inset-y-0 left-0 overflow-hidden whitespace-nowrap" style={{ width: `${anteil * 100}%` }}>
                ★
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
  return (
    <section className="maestro-druck-aus space-y-3" data-testid="v2-bewertungen" aria-labelledby="v2-bewertungen-titel">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="v2-bewertungen-titel" className="font-serif text-headline-md text-on-background dark:text-inverse-on-surface">
          {t.bewertungenTitel}
        </h2>
        <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          <span className="text-secondary dark:text-secondary-fixed-dim" aria-label={t.sterneAria(note)}>
            {sterne(b.sterne)}
          </span>{" "}
          {t.bewertungSumme(note, anzahl, b.anzahl === 1)}
        </p>
      </div>
      {b.rezensionen.length > 0 && (
        // Antoine, 13.10.2026 („Dritte Google-Bewertung wird nicht angezeigt"): Google liefert höchstens 5
        // Rezensionen, davon bleiben nach dem 5-Sterne-Filter oft nur 2 — dann zwei breite Karten statt Lücke.
        <div className={`grid gap-3 ${b.rezensionen.length === 1 ? "" : b.rezensionen.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
          {b.rezensionen.map((r, i) => (
            <Card key={i} className="p-4" data-testid="v2-rezension">
              {r.sterne != null && (
                <p className="text-secondary dark:text-secondary-fixed-dim" aria-label={t.sterneAria(zahl.format(r.sterne))}>
                  {sterne(r.sterne)}
                </p>
              )}
              <p className="mt-2 line-clamp-6 font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">„{r.text}“</p>
              <p className="mt-2 font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">
                <a href={r.autorUrl} target="_blank" rel="noopener noreferrer" className="font-semibold hover:underline">
                  {r.autor}
                </a>
                {r.zeit ? ` · ${r.zeit}` : ""}
              </p>
            </Card>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-3xl font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim" data-testid="v2-bewertung-hinweis">
          {t.bewertungHinweis(betrieb)}
        </p>
        <div className="flex items-center gap-3">
          <a href={b.mapsUrl} target="_blank" rel="noopener noreferrer" className="font-body-sm text-[12px] text-on-surface underline underline-offset-2 dark:text-inverse-on-surface">
            {t.alleBewertungen}
          </a>
          <GoogleAttribution werte={[b.sterne, b.anzahl]} />
        </div>
      </div>
    </section>
  );
}

/** „Noch eine Frage?“ — öffnet den bestehenden Nachrichtenweg der Seite (kein neuer Versandweg). */
export function FrageKarte({
  v2,
  lang,
  onFrage,
  deaktiviert,
}: {
  v2: AngebotsseiteV2;
  lang: Sprache;
  onFrage: () => void;
  deaktiviert?: boolean;
}) {
  const t = V2T[lang];
  const a = v2.ansprechpartner;
  const vorname = vornameAus(a);
  return (
    <Card className="maestro-druck-aus flex flex-col gap-4 p-5 sm:flex-row sm:items-center" data-testid="v2-frage">
      {a ? <Avatar a={a} /> : <Icon name="chat" className="text-[26px] text-secondary dark:text-secondary-fixed-dim" />}
      <div className="min-w-0 flex-1">
        <p className="font-body-md text-body-md font-semibold text-on-surface dark:text-inverse-on-surface">{t.frageTitel}</p>
        <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim" data-testid="v2-frage-antwortzeit">
          {a && v2.antwortDauer ? t.frageAntwort(vorname ?? a.name, t.dauer(v2.antwortDauer.wert, v2.antwortDauer.einheit)) : t.frageAntwortOhne}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {a?.telefon && (
          <a
            href={`tel:${a.telefon.trim().startsWith("+") ? "+" : ""}${a.telefon.replace(/\D/g, "")}`}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 font-label-md text-label-md text-on-surface-variant hover:bg-surface-container-high dark:text-secondary-fixed-dim"
          >
            <Icon name="call" className="text-[17px]" /> {t.anrufen}
          </a>
        )}
        <Button type="button" variant="secondary" onClick={onFrage} disabled={deaktiviert} data-testid="v2-frage-knopf">
          <Icon name="chat" className="text-[17px]" /> {t.frageKnopf(vorname)}
        </Button>
      </div>
    </Card>
  );
}

/**
 * „Mit Mitentscheider teilen“: Web Share API, sonst Link in die Zwischenablage, sonst den Link
 * anzeigen. Geteilt wird DERSELBE Gast-Link (`/angebot/<token>`) — kein neuer Token.
 */
export function TeilenKnopf({ url, lang, betrieb, deaktiviert }: { url: string | null; lang: Sprache; betrieb: string; deaktiviert?: boolean }) {
  const t = V2T[lang];
  const [stand, setStand] = useState<"offen" | "kopiert" | "fehler">("offen");
  async function teilen() {
    if (!url || deaktiviert) return;
    const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
    if (typeof nav.share === "function") {
      try {
        await nav.share({ title: t.teilenTitel(betrieb), url });
        return;
      } catch (e) {
        // Der Gast hat den Teilen-Dialog geschlossen — das ist keine Störung.
        if (e instanceof Error && e.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setStand("kopiert");
    } catch {
      setStand("fehler");
    }
  }
  return (
    <div className="maestro-druck-aus text-center" data-testid="v2-teilen">
      <Button type="button" variant="secondary" className="w-full sm:w-auto" onClick={() => void teilen()} disabled={!url || deaktiviert} data-testid="v2-teilen-knopf">
        <Icon name="share" className="text-[17px]" /> {t.teilen}
      </Button>
      <p className="mt-2 font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim" aria-live="polite">
        {stand === "kopiert" ? t.kopiert : stand === "fehler" ? `${t.kopierenFehler} ${url ?? ""}` : t.teilenHinweis}
      </p>
    </div>
  );
}

/**
 * Feste Aktionsleiste am unteren Bildschirmrand (Antoine, 14.10.2026: „Die Zahlbuttons und
 * Annehmbuttons unten immer als fixe Leiste, die immer zu sehen ist. So machen, das es gut
 * aussieht und nicht zu viel platz einnimmt."). Links knapp Betrag/Kontext, rechts der jeweils
 * aktuelle Hauptknopf. Nur Huelle: Knopf, Bedingungen und Texte bringt der Aufrufer mit.
 * Im Druck weg; die Seite haelt unten Platz frei (`FESTE_LEISTE_PLATZ`).
 */
export function FesteLeiste({
  info,
  hinweis,
  hinweisTestId,
  testId,
  children,
}: {
  info: ReactNode;
  hinweis?: ReactNode;
  hinweisTestId?: string;
  testId: string;
  children: ReactNode;
}) {
  return (
    <div
      className="maestro-druck-aus fixed inset-x-0 bottom-0 z-40 border-t border-outline-variant/60 bg-surface-container-lowest/90 pb-[env(safe-area-inset-bottom)] shadow-[0_-8px_18px_-12px_rgba(45,38,26,0.3)] backdrop-blur print:hidden dark:border-outline/30 dark:bg-inverse-surface/90"
      data-testid={testId}
    >
      <div className="mx-auto flex min-h-16 max-w-5xl items-center gap-3 px-4 py-2 md:px-margin-desktop">
        <div className="min-w-0 flex-1">
          <div className="truncate font-body-sm text-body-sm font-semibold tabular-nums text-on-surface dark:text-inverse-on-surface" data-testid={`${testId}-info`}>
            {info}
          </div>
          {hinweis && (
            <p className="line-clamp-2 text-[12px] leading-4 text-secondary dark:text-secondary-fixed-dim" data-testid={hinweisTestId}>
              {hinweis}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">{children}</div>
      </div>
    </div>
  );
}

/** Platz unten, damit die feste Leiste nichts verdeckt (min-h-16 + Rand + Sicherheitsabstand). */
export const FESTE_LEISTE_PLATZ = "pb-24";

/**
 * Leiste vor der Zusage (Schritt 1): gewählte Variante, Summe (Server), Knopf zum Annahmebereich.
 * Der Knopf SCROLLT nur — angenommen wird ausschließlich mit dem Knopf „… zahlungspflichtig annehmen“.
 */
export function MobileLeiste({
  titel,
  gaeste,
  gesamtCents,
  mitZubuchungen,
  lang,
  onWeiter,
  aLaCarte = false,
}: {
  /** Migration 731: „À la carte“ - keine Summe, die Leiste sagt „Nach Karte“. */
  aLaCarte?: boolean;
  titel: string;
  gaeste: number;
  gesamtCents: number;
  mitZubuchungen: boolean;
  lang: Sprache;
  onWeiter: () => void;
}) {
  const t = V2T[lang];
  return (
    <FesteLeiste
      testId="v2-mobile-leiste"
      info={
        <>
          <span data-testid="v2-leiste-summe">{aLaCarte ? A_LA_CARTE_GAST[lang].nachKarte : money(gesamtCents)}</span>
          <span className="font-normal text-on-surface-variant dark:text-secondary-fixed-dim">
            {" · "}
            {titel}
            {gaeste > 0 ? ` · ${t.leisteGaeste(gaeste)}` : ""}
          </span>
        </>
      }
      hinweis={mitZubuchungen ? t.leisteZubuchungen : undefined}
    >
      <Button type="button" className="!px-4 !py-2.5" onClick={onWeiter} data-testid="v2-leiste-knopf">
        {t.leisteKnopf}
        <Icon name="arrow_downward" className="text-[18px]" />
      </Button>
    </FesteLeiste>
  );
}

/** Weich zu einem Element scrollen — ohne Fehler, wo der Browser (oder die Testumgebung) es nicht kann. */
export function scrolleZu(id: string): void {
  const ziel = typeof document !== "undefined" ? document.getElementById(id) : null;
  if (ziel && typeof ziel.scrollIntoView === "function") ziel.scrollIntoView({ behavior: "smooth", block: "start" });
}
